import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  FC,
  PropsWithChildren,
} from 'react';
import { ILayoutContextProps } from './types';
import { useAuthentication } from '@context/businessContainer';
import { useRouter } from 'next/router';
import { getMobileTitle } from './helper';
import {
  ICartSharedContents,
  ICartSharedSubscriptionTimeline,
  IInformationsModal,
  MegaMenu,
  MegamenuDocument,
  MegamenuentryDocument,
} from '@monofront/types';
import { buildMegamenu, formatPrismicData } from '@monofront/business/utils';
import { useTagContext } from '@hooks/businessContainer';
import { useMediaQuery } from '@monofront/business/hooks';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

//  If React can’t find any providers of that particular context in the parent tree,
//  the context value returned by useContext() will be equal to the default value specified here
//  In our case, the provider is defined here, so we can force it as ILayoutContextProps to prevent (type | undefined)
const LayoutContext = createContext<ILayoutContextProps>(
  {} as ILayoutContextProps,
);

type LayoutContextProps = {
  value: {
    layout: any;
    megamenu: MegamenuDocument | null;
    megamenuEntries: MegamenuentryDocument[];
    props: any;
  };
};

const LayoutContextProvider: FC<PropsWithChildren<LayoutContextProps>> = ({
  value,
  children,
}) => {
  const { isMember, isAuthenticated } = useAuthentication();
  const router = useRouter();
  const isHomepageNonMember = router.pathname === '/' && !isMember;
  const [customTitleHeaderMobile, setCustomTitleHeaderMobile] = useState<
    null | string
  >(null);
  const isHomepageMember = Boolean(router.route === '/home' && isMember);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(102);
  const shopLayout = value.layout[0].data;

  const megamenuContent = value.megamenu?.data;
  const megamenuEntries = value.megamenuEntries;

  const updateMainHeaderHeight = useCallback(() => {
    setMainHeaderHeight(
      [...document.querySelectorAll('header.main-header')].reduce(
        (acc, el) => Math.max(acc, el?.getBoundingClientRect?.()?.bottom),
        0,
      ),
    );
  }, []);

  const [cartSharedContents, cartSharedSubscriptionTimeline] = useMemo(
    () => [
      formatPrismicData(
        value.layout[0].data.cart_shared_contents.data?.body.find(
          (slice) => slice.slice_type === 'cart_shared_contents',
        ),
      ) as ICartSharedContents,
      formatPrismicData(
        value.layout[0].data.cart_shared_contents.data?.body.find(
          (slice) => slice.slice_type === 'subscription_timeline',
        ),
      ) as ICartSharedSubscriptionTimeline,
    ],
    [value.layout[0].data.cart_shared_contents.data],
  );

  useEffect(() => {
    updateMainHeaderHeight();
  }, [router.pathname, isAuthenticated]);

  useEffect(() => {
    window.addEventListener('resize', updateMainHeaderHeight);
    window.addEventListener('scroll', updateMainHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateMainHeaderHeight);
      window.removeEventListener('scroll', updateMainHeaderHeight);
    };
  }, []);

  const announcement = useMemo(
    () => ({
      text: shopLayout?.announcement_v2,
      link: shopLayout?.link,
    }),
    [shopLayout],
  );
  const headerTop = shopLayout.headertop;
  const headerTopBanners = shopLayout.body1;
  const informationsModal: IInformationsModal = shopLayout.body2?.find(
    ({ slice_type }) => slice_type === 'informationsmodal',
  )?.items?.[0];

  const accountMenuDesktop = shopLayout.desktopaccountmenu;

  const { legal_notice: legalNotice } = shopLayout;

  const isMobile = useMediaQuery(800);
  const tagContext = useTagContext({ isWebMobile: isMobile });

  const isMegaMenuAbTestEnabled = useFeatureIsOn(
    megamenuContent?.feature_flag_key ?? '',
  );

  const megamenu = useMemo<MegaMenu>(() => {
    if (!megamenuContent) {
      return { collections: [], highlightedLinks: [] };
    }

    const highlightedLinks = isMegaMenuAbTestEnabled
      ? megamenuContent.abtests_highlighted_links
      : megamenuContent.highlighted_links;

    const collections = isMegaMenuAbTestEnabled
      ? megamenuContent.abtests_collections
      : megamenuContent.collections;

    return buildMegamenu(
      highlightedLinks,
      collections,
      megamenuEntries,
      tagContext,
    );
  }, [megamenuContent, megamenuEntries, tagContext, isMegaMenuAbTestEnabled]);

  const footer = useMemo(
    () => formatPrismicData(shopLayout.footer),
    [shopLayout.footer],
  );

  const mobileTitle = getMobileTitle(
    megamenu,
    value.props,
    router,
    customTitleHeaderMobile,
  );

  const previousPageLabel = value.props.previousPageLabel;
  const previousPageSlug = value.props.previousPageSlug;

  const seoCollectionsRating = shopLayout.seo_collections_rating;
  const seoCollectionsVolume = shopLayout.seo_collections_volume;

  const errorTitle = shopLayout.error_page_title;
  const errorText = shopLayout.error_page_text;
  const errorImage = shopLayout.error_page_image;

  const memoizedContextValue = useMemo(
    () => ({
      announcement,
      headerTop,
      headerTopBanners,
      accountMenuDesktop,
      megamenu,
      footer,
      legalNotice,
      isHomepageNonMember,
      isHomepageMember,
      mobileTitle,
      setCustomTitleHeaderMobile,
      previousPageLabel,
      previousPageSlug,
      mainHeaderHeight,
      updateMainHeaderHeight,
      cartSharedContents,
      cartSharedSubscriptionTimeline,
      informationsModal,
      seoCollectionsRating,
      seoCollectionsVolume,
      errorTitle,
      errorText,
      errorImage,
      errorSlices: shopLayout.error_page_slices?.length
        ? shopLayout.error_page_slices.flatMap(({ slice }) => [
            ...slice.data.body,
          ])
        : [],
    }),
    [
      announcement,
      headerTop,
      headerTopBanners,
      megamenu,
      accountMenuDesktop,
      footer,
      legalNotice,
      isHomepageNonMember,
      isHomepageMember,
      mobileTitle,
      setCustomTitleHeaderMobile,
      previousPageLabel,
      previousPageSlug,
      mainHeaderHeight,
      updateMainHeaderHeight,
      cartSharedContents,
      cartSharedSubscriptionTimeline,
      informationsModal,
      seoCollectionsRating,
      seoCollectionsVolume,
      errorTitle,
      errorText,
      errorImage,
      shopLayout.error_page_slices,
    ],
  );

  return (
    <LayoutContext.Provider value={memoizedContextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => useContext(LayoutContext);

export { LayoutContextProvider, LayoutContext };
